function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : null;
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function formatPrice(value) {
  return finiteNumber(value) !== null
    ? `$${value.toFixed(2)}`
    : "unavailable";
}

export function analyzeLiveNinaDecision({
  trade = {},
  dataStatus = "ready",
  isStale = false,
} = {}) {
  const currentPrice = finiteNumber(trade.currentPrice);

  if (dataStatus !== "ready" || isStale || currentPrice === null) {
    return {
      decision: isStale
        ? "WAIT — STALE MARKET DATA"
        : "WAIT — LIVE DATA UNAVAILABLE",
      confidence: 0,
      risk: "High",
      reason:
        "The AI Assistant requires a fresh authenticated market price before producing a live decision.",
      nextTrigger: "Restore fresh authenticated market data",
      status: "DATA BLOCKED",
      grade: "—",
      tradeStage: "BLOCKED",
      suggestedAction: "Do not trade from this workspace",
    };
  }

  const vwap = finiteNumber(trade.vwap);
  const previousClose = finiteNumber(trade.previousClose);
  const dayHigh = finiteNumber(trade.dayHigh);
  const dayLow = finiteNumber(trade.dayLow);
  const bid = finiteNumber(trade.bid);
  const ask = finiteNumber(trade.ask);

  let bullishSignals = 0;
  let bearishSignals = 0;
  let evidenceSlots = 0;
  const evidence = [];

  if (vwap !== null && vwap > 0) {
    evidenceSlots += 1;

    if (currentPrice > vwap) {
      bullishSignals += 1;
      evidence.push(
        `Price ${formatPrice(currentPrice)} is above VWAP ${formatPrice(vwap)}.`
      );
    } else if (currentPrice < vwap) {
      bearishSignals += 1;
      evidence.push(
        `Price ${formatPrice(currentPrice)} is below VWAP ${formatPrice(vwap)}.`
      );
    } else {
      evidence.push("Price is at VWAP.");
    }
  }

  if (previousClose !== null && previousClose > 0) {
    evidenceSlots += 1;

    if (currentPrice > previousClose) {
      bullishSignals += 1;
      evidence.push("Price is above the previous regular-session close.");
    } else if (currentPrice < previousClose) {
      bearishSignals += 1;
      evidence.push("Price is below the previous regular-session close.");
    }
  }

  const hasDayRange =
    dayHigh !== null &&
    dayLow !== null &&
    dayHigh > dayLow;

  if (hasDayRange) {
    evidenceSlots += 1;

    const rangePosition =
      (currentPrice - dayLow) / (dayHigh - dayLow);

    if (rangePosition >= 0.65) {
      bullishSignals += 1;
      evidence.push(
        "Price is trading in the upper portion of the authenticated day range."
      );
    } else if (rangePosition <= 0.35) {
      bearishSignals += 1;
      evidence.push(
        "Price is trading in the lower portion of the authenticated day range."
      );
    } else {
      evidence.push(
        "Price is trading near the middle of the authenticated day range."
      );
    }
  }

  let spreadPercent = null;
  let risk = "Unknown";

  if (
    bid !== null &&
    ask !== null &&
    bid > 0 &&
    ask >= bid
  ) {
    evidenceSlots += 1;

    spreadPercent = ((ask - bid) / currentPrice) * 100;

    if (spreadPercent > 0.5) {
      risk = "High";
    } else if (spreadPercent > 0.25) {
      risk = "Moderate";
    } else {
      risk = "Low";
    }

    evidence.push(
      `Authenticated bid/ask spread is ${spreadPercent.toFixed(2)}%.`
    );
  }

  if (risk === "Low" && bearishSignals >= 2) {
    risk = "Moderate";
  }

  const directionalSignals =
    bullishSignals + bearishSignals;

  const consensus =
    directionalSignals > 0
      ? Math.abs(bullishSignals - bearishSignals) /
        directionalSignals
      : 0;

  const confidence = Math.round(
    clamp(
      (evidenceSlots / 4) * 60 +
        consensus * 35,
      0,
      95
    )
  );

  let decision = "WAIT — MIXED LIVE CONDITIONS";
  let nextTrigger =
    "Wait for stronger aligned authenticated evidence";
  let suggestedAction = "Continue monitoring";
  let status = "MONITORING";
  let tradeStage = "WAITING";

  if (risk === "High") {
    decision = "DO NOT ENTER — WIDE SPREAD";
    nextTrigger =
      "Wait for the authenticated bid/ask spread to tighten";
    suggestedAction = "Protect capital and wait";
    status = "BLOCKED";
    tradeStage = "BLOCKED";
  } else if (
    bullishSignals >= 2 &&
    bullishSignals > bearishSignals
  ) {
    decision = "WATCH LONG SETUP";
    nextTrigger =
      "Require confirmation while price continues holding above VWAP";
    suggestedAction =
      "Watch for confirmation; do not chase the move";
    status = "MONITORING";
    tradeStage = "SETUP FOUND";
  } else if (
    bearishSignals >= 2 &&
    bearishSignals > bullishSignals
  ) {
    decision = "WAIT — BEARISH PRESSURE";
    nextTrigger =
      "Require a VWAP reclaim and improved authenticated price structure";
    suggestedAction =
      "Stay defensive and wait for conditions to improve";
  }

  const grade =
    risk === "High"
      ? "D"
      : confidence >= 85
      ? "A"
      : confidence >= 70
      ? "B+"
      : confidence >= 55
      ? "B"
      : "C";

  return {
    decision,
    confidence,
    risk,
    reason:
      evidence.length > 0
        ? evidence.join(" ")
        : "Fresh authenticated price is connected, but additional live evidence is unavailable.",
    nextTrigger,
    status,
    grade,
    tradeStage,
    suggestedAction,
    evidenceCount: evidenceSlots,
    bullishSignals,
    bearishSignals,
    spreadPercent,
  };
}